import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Link,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { StyledTableRow } from "../../../Utils/styledcell";

export default function InvoicesTable({
  rows,
  isMobile,
  onEdit,
  onConfirm,
  formatAmount,
  getPaymentName,
}) {
  /* -------------------------------------------------- mobile-card helpers */
  const [anchorEl, setAnchorEl]   = useState(null);
  const [selectedInv, setSelectedInv] = useState(null);
  const openMenu   = (e, inv) => { setAnchorEl(e.currentTarget); setSelectedInv(inv); };
  const closeMenu  = () => { setAnchorEl(null); setSelectedInv(null); };

  const handleEdit    = () => { selectedInv && onEdit(selectedInv);  closeMenu(); };
  const handleConfirm = () => { selectedInv && onConfirm(selectedInv.id); closeMenu(); };

  /* -------------------------------------------------- MOBILE  */
  if (isMobile) {
    return (
      <>
        <Box sx={{ display: "grid", gap: 2 }}>
          {rows.map((inv) => (
            <Card
              key={inv.id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                transition: "transform .3s",
                "&:hover": { transform: "translateY(-3px)" },
              }}
            >
              <CardContent>
                {/* Top line */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {getPaymentName(inv)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  PO&nbsp;#: {inv.po_number || "N/A"}
                </Typography>

                {/* Amount + status */}
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {formatAmount(inv.amount_due)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        inv.status === "pending"
                          ? "#ed6c02"
                          : inv.status === "Paid"
                          ? "#2e7d32"
                          : "#9e9e9e",
                    }}
                  >
                    {inv.status}
                  </Typography>
                </Box>

                {inv.notes && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: "italic", whiteSpace: "pre-wrap" }}
                  >
                    {inv.notes}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {/* link & overflow menu */}
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Link
                    href={`https://www.useblitz.co/invoicing/${inv.id}`}
                    target="_blank"
                    underline="hover"
                  >
                    View Invoice
                  </Link>

                  <IconButton size="small" onClick={(e) => openMenu(e, inv)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Action menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          {selectedInv?.status === "pending" && (
            <MenuItem onClick={handleConfirm}>Confirm</MenuItem>
          )}
          {selectedInv?.status !== "Paid" && (
            <MenuItem onClick={handleEdit}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
            </MenuItem>
          )}
        </Menu>
      </>
    );
  }

  /* -------------------------------------------------- DESKTOP TABLE */
  return (
    <Table sx={{ minWidth: 700 }} aria-label="invoices table">
      <TableHead>
        <TableRow>
          {[
            "Payment Name",
            "PO #",
            "Amount Due",
            "Status",
            "Created At",
            "Notes",
            "Invoice Link",
            "Edit / Confirm",
          ].map((h) => (
            <TableCell key={h} align="center">
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((inv) => (
          <StyledTableRow key={inv.id}>
            <TableCell align="center">{getPaymentName(inv)}</TableCell>
            <TableCell align="center">{inv.po_number || "N/A"}</TableCell>
            <TableCell align="center">{formatAmount(inv.amount_due)}</TableCell>
            <TableCell align="center">{inv.status}</TableCell>
            <TableCell align="center">
              {new Date(inv.created_at).toLocaleDateString()}
            </TableCell>
            <TableCell align="center">{inv.notes}</TableCell>
            <TableCell align="center">
              <Link
                href={`https://www.useblitz.co/invoicing/${inv.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Invoice
              </Link>
            </TableCell>
            <TableCell align="center">
              {inv.status === "pending" && (
                <Button variant="contained" color="success" onClick={() => onConfirm(inv.id)}>
                  Confirm
                </Button>
              )}
              {inv.status !== "Paid" && (
                <Button startIcon={<EditIcon />} sx={{ ml: 1 }} onClick={() => onEdit(inv)}>
                  Edit
                </Button>
              )}
            </TableCell>
          </StyledTableRow>
        ))}
      </TableBody>
    </Table>
  );
}
